import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { Store } from '@ngrx/store';

import { Subject, Observable, combineLatest } from 'rxjs';
import { takeUntil, map } from 'rxjs/operators';

import { AppState, Selectors, Actions } from 'store/root-reducer';
import { CustomerActionTypes } from 'store/actions/customer.actions';
import { SearchActionTypes } from 'store/actions/search.actions';
import { BillableActionTypes } from 'store/actions/billable.actions';

import { RecordType } from 'modules/shared/models/record.models';
import { UserNotificationRow } from 'modules/shared/data-source/user-notifications.data-source';
import { Domain } from 'modules/shared/models/domain.models';
import { CurrentUser } from 'modules/shared/models/account.models';
import {
  CreateOrUpdateCustomerRequest, CreateOrUpdateCustomerPayload, GetCurrentDomainDefaultNotifiedUsersPayload
} from 'store/models/customer.models';
import { CheckBalancePayload } from 'store/models/billable.models';

import { IBreadcrumbs } from 'modules/shared/components/breadcrumbs/breadcrumbs.component';

import { UserNotificationsComponent } from 'modules/shared/components/user-notifications/user-notifications.component';
import { CustomerService } from 'modules/core/services/customer.service';

export interface IUserNotificationRow {
  name: string;
  email: string;
  L1: boolean;
  L2: boolean;
  L3: boolean;
  id: string;
}

@Component({
  selector: 'artemis-customer-onboarding-page',
  templateUrl: './customer-onboarding.page.html',
  styleUrls: ['./customer-onboarding.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerOnboardingPage implements OnInit, OnDestroy {

  @ViewChild(UserNotificationsComponent, {static: false}) userNotificationsComponent: UserNotificationsComponent;

  userList: Array<IUserNotificationRow>;

  notifyUserList: string[] = [];
  currentUser: string;

  displayedColumns: string[] = ['user', 'l1', 'l2', 'l3'];

  individualForm: any;
  corporateForm: any;
  customerType: any;
  customerId: any;
  recordId: any;
  individualFormValid: boolean = false;
  corporateFormValid: boolean = false;
  domain: Domain;
  unsubscribe$: Subject<any> = new Subject();

  breadcrumbs: Array<IBreadcrumbs> = [
    {
      url: '/customers',
      label: 'Customer List'
    }, {
      url: '/customers/onboarding',
      label: 'Onboarding - Manual Input'
    }
  ];

  currentDomainDefaultUserList: Array<UserNotificationRow>;
  defaultUsersCardActive: boolean = true;
  defaultUsersListLoading$: Observable<boolean>;

  formLoading: boolean = false;
  customerLoading$: Observable<boolean>;
  dataAvaliableFormFields: any;

  triedSubmit: boolean = false;

  constructor(private store: Store<AppState>,
              private cdRef: ChangeDetectorRef,
              private customerService: CustomerService,
              private router: Router) { }

  ngOnInit() {
    this.getLoadingStatus();

    this.initUsers();
    this.store.select(Selectors.domain.getCurrentDomain)
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe((domain: Domain) => {
        if (domain) {
          this.domain = domain;

          this.store.dispatch(new Actions.customer.GetDefaultNotifiedUsers(<GetCurrentDomainDefaultNotifiedUsersPayload>{
            domain: domain
          }));
        }
      });

    this.store
      .select(Selectors.customer.getDefaultNotifiedUsers)
      .pipe(
        takeUntil(this.unsubscribe$)
      )
      .subscribe(
        (defaultUsers: Array<UserNotificationRow>) => {
          this.currentDomainDefaultUserList = defaultUsers;
          this.checkCurrentUsersCard();
          this.cdRef.detectChanges();
        }
      );
        this.customerService.getAppConfig()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(item => {
          this.dataAvaliableFormFields = item;
        });
  }

  /**
   * Get loading status from store
   * @method getLoadingStatus
   */
  private getLoadingStatus(): void {
    this.customerLoading$ = combineLatest([
      this.store.select(Selectors.customer.getLoading),
      this.store.select(Selectors.search.getLoading),
      this.store.select(Selectors.billable.getLoading)
    ]).pipe(
      takeUntil(this.unsubscribe$),
      map(([customerLoading, screeningLoading, billableLoading]:
        [Array<CustomerActionTypes>, Array<SearchActionTypes>, Array<BillableActionTypes>]) => {
        return customerLoading.includes(CustomerActionTypes.CREATE_DRAFT_CUSTOMER) ||
               customerLoading.includes(CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER) ||
               customerLoading.includes(CustomerActionTypes.CREATE_AND_CRP_CUSTOMER) ||
               billableLoading.includes(BillableActionTypes.CHECK_BALANCE) ||
               screeningLoading.includes(SearchActionTypes.POST_SCREENING);
      })
    );

    this.defaultUsersListLoading$ = this.store.select(Selectors.customer.getLoading)
      .pipe(
        takeUntil(this.unsubscribe$),
        map((customerLoading: Array<CustomerActionTypes>) => {
          return customerLoading.includes(CustomerActionTypes.SET_DEFAULT_NOTIFIED_USERS) ||
                 customerLoading.includes(CustomerActionTypes.GET_DEFAULT_NOTIFIED_USERS);
        })
      );
  }

  private checkCurrentUsersCard(): void {
    if (!this.currentDomainDefaultUserList || !this.currentDomainDefaultUserList.length) {
      this.defaultUsersCardActive = false;
      this.cdRef.detectChanges();
      this.userNotificationsComponent.dataSource.selectCurrentUser(this.currentUser);
    } else if (this.domain) {
      this.defaultUsersCardActive = true;
      this.cdRef.detectChanges();
    }
  }

  private initUsers(): void {
    this.store
      .select(Selectors.auth.getUser)
      .pipe(
        takeUntil(this.unsubscribe$)
      ).subscribe((currentUser: CurrentUser) => {
        if (currentUser) {
          this.currentUser = currentUser.sub;
          this.cdRef.detectChanges();
        }
      });
  }

  isFormValid(): boolean {
    return (this.customerType === RecordType.INDIVIDUAL && this.individualFormValid) ||
      (this.customerType === RecordType.CORPORATE && this.corporateFormValid);
  }

  /**
   * Check if at lease one L1, one L2 and one L3 selected
   * @method isNotifyUserValid
   */
  isNotifyUserValid(): boolean {
    if (this.userNotificationsComponent) {
      return this.userNotificationsComponent.dataSource.getSelectedUsers().some((selectedUser: UserNotificationRow) => selectedUser.l1) &&
             this.userNotificationsComponent.dataSource.getSelectedUsers().some((selectedUser: UserNotificationRow) => selectedUser.l2) &&
             this.userNotificationsComponent.dataSource.getSelectedUsers().some((selectedUser: UserNotificationRow) => selectedUser.l3);
    } else {
      return this.defaultUsersCardActive && this.currentDomainDefaultUserList &&
             this.currentDomainDefaultUserList.some((selectedUser: UserNotificationRow) => selectedUser.l1) &&
             this.currentDomainDefaultUserList.some((selectedUser: UserNotificationRow) => selectedUser.l2) &&
             this.currentDomainDefaultUserList.some((selectedUser: UserNotificationRow) => selectedUser.l3);
    }
  }

  /**
   * Check if user has entered any alias name or former registered name
   * @method hasFormerOrAliasName
   */
  hasFormerOrAliasName(): boolean {
    if (this.customerType === RecordType.INDIVIDUAL) {
      return this.individualForm.aliasNames.length || this.individualForm.oldNames.length;
    } else if (this.customerType === RecordType.CORPORATE) {
      return this.corporateForm.aliasNames.length || this.corporateForm.oldNames.length;
    }

    return false;
  }

  registerNewCustomer(subaction?: string) {
    this.triedSubmit = true;
    if (this.isFormValid() && this.isNotifyUserValid()) {
      let recordPayload;
      if (this.defaultUsersCardActive) {
        this.notifyUserList = this.currentDomainDefaultUserList.map((user: UserNotificationRow) => user.oauthId);
      } else {
        this.notifyUserList = this.userNotificationsComponent.dataSource
          .getSelectedUsers()
          .map((user: UserNotificationRow) => user.oauthId);
      }

      const formName: string = this.customerType === RecordType.INDIVIDUAL ? 'individualForm' : 'corporateForm';
      const customerDataToSend: CreateOrUpdateCustomerRequest = <CreateOrUpdateCustomerRequest>{
        users: this.notifyUserList,
        domains: [this.domain.id],
        paymentModes: this[formName].paymentModes,
        onboardingMode: this[formName].onboardingMode,
        productServiceComplexity: this[formName].productServiceComplexity,
        natureOfBusinessRelationship: this[formName].natureOfBusinessRelationship,
        referenceId: this[formName].customerReferenceId,
        isActiveCustomer: this[formName].isActiveCustomer
      };

      if (this.customerType === RecordType.INDIVIDUAL) {
        recordPayload = {
          title: this.individualForm.title,
          name: this.individualForm.name,
          aliasNames: this.individualForm.aliasNames,
          oldNames: this.individualForm.oldNames,
          nationality: this.individualForm.nationality,
          countryOfResidence: this.individualForm.countryOfResidence,
          gender: this.individualForm.gender,
          dateOfBirth: this.individualForm.dateOfBirth,
          industry: this.individualForm.industry,
          occupation: this.individualForm.occupation,
          addresses: this.individualForm.addresses,
          phoneNumbers: this.individualForm.phoneNumbers,
          referenceId: this.individualForm.recordReferenceId,
          sourceOfFunds: this.individualForm.sourceOfFunds,
          emailAddresses: this.individualForm.emailAddresses,
          bankAccounts: this.individualForm.bankAccounts,
          countryOfBirth: this.individualForm.countryOfBirth,
          idType: this.individualForm.idType,
          idNumber: this.individualForm.idNumber,
          idIssueDate: this.individualForm.idIssueDate,
          idExpiryDate: this.individualForm.idExpiryDate,
          primary: true,
        };

        if (this.recordId) {
          recordPayload['id'] = this.recordId;
        }
        customerDataToSend['sourceOfWealth'] = this.individualForm.sourceOfWealth;
        customerDataToSend['annualIncomeLevel'] = this.individualForm.annualIncomeLevel;
        customerDataToSend['policyCount'] = this.individualForm.policyCount;
        customerDataToSend['premiumAmount'] = this.individualForm.premiumAmount;
        customerDataToSend.individualRecords = [recordPayload];
      } else if (this.customerType === RecordType.CORPORATE) {
        recordPayload = {
          isIncorporated: !!this.corporateForm.isIncorporated,
          name: this.corporateForm.name,
          aliasNames: this.corporateForm.aliasNames,
          oldNames: this.corporateForm.oldNames,
          entityType: this.corporateForm.entityType,
          ownershipStructureLayers: this.corporateForm.ownershipStructureLayers,
          countryOfIncorporation: this.corporateForm.countryOfIncorporation,
          countryOfOperations: this.corporateForm.countryOfOperations,
          businessActivity: this.corporateForm.businessActivity,
          addresses: this.corporateForm.addresses,
          phoneNumbers: this.corporateForm.phoneNumbers,
          referenceId: this.corporateForm.recordReferenceId,
          sourceOfFunds: this.corporateForm.sourceOfFunds,
          emailAddresses: this.corporateForm.emailAddresses,
          bankAccounts: this.corporateForm.bankAccounts,
          incorporationNumber: this.corporateForm.incorporationNumber,
          website: this.corporateForm.website,
          incorporationDate: this.corporateForm.incorporationDate,
          incorporationExpiryDate: this.corporateForm.incorporationExpiryDate,
          inFatfJurisdiction: this.corporateForm.inFatfJurisdiction,
          imoNumber: this.corporateForm.imoNumber,
          primary: true
        };

        if (this.recordId) {
          recordPayload['id'] = this.recordId;
        }
        customerDataToSend.corporateRecords = [recordPayload];
        customerDataToSend['policyCount'] = this.corporateForm.policyCount;
        customerDataToSend['premiumAmount'] = this.corporateForm.premiumAmount;
      }

      if (subaction === 'ra') {
        this.store.dispatch(new Actions.billable.CheckBalance(<CheckBalancePayload>{
          actions: [
            new Actions.customer.CreateAndRACustomer(
              <CreateOrUpdateCustomerPayload>{
                customerData: customerDataToSend,
                mode: 'CREATE'
              }
            )
          ]
        }));
      } else if (subaction === 'crp' && this.customerType === RecordType.CORPORATE) {
        this.store.dispatch(new Actions.customer.CreateAndCRPCustomer(
          <CreateOrUpdateCustomerPayload>{
            customerData: customerDataToSend,
            mode: 'CREATE'
          }
        ));
      } else {
        this.store.dispatch(new Actions.customer.CreateDraftCustomer(
          <CreateOrUpdateCustomerPayload>{
            customerData: customerDataToSend,
            mode: 'CREATE'
          }
        ));
      }
    }
  }

  childDataChanged(data: any) {
    this.individualForm = data.individualForm;
    this.corporateForm = data.corporateForm;
    this.individualFormValid = data.individualFormValid;
    this.corporateFormValid = data.corporateFormValid;
    this.customerType = data.customerType;
    this.recordId = data.recordId;
  }

  formLoadingHandler(formLoading: boolean) {
    this.formLoading = formLoading;
  }

  toggleUsersCards(): void {
    this.defaultUsersCardActive = !this.defaultUsersCardActive;
    this.cdRef.detectChanges();
  }

  updateDefaultSettings(): void {
    this.router.navigateByUrl('account-settings');
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
